import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { ArrowLeft } from 'lucide-react';

const ClientForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // present when editing
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    address: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      fetchClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClient = async () => {
    try {
      const res = await apiClient.get(`/clients/${id}`);
      const { name, email, company, address, phone } = res.data;
      setFormData({ name: name || '', email: email || '', company: company || '', address: address || '', phone: phone || '' });
    } catch (err) {
      setError('Failed to load client data.');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Full Name is required.';
      case 'email':
        if (!value.trim()) return 'Email is required.';
        return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
          ? ''
          : 'Please enter a valid email address.';
      case 'phone':
        if (!value.trim()) return ''; // optional field
        return /^[+]?[\d\s\-().]{7,20}$/.test(value)
          ? ''
          : 'Please enter a valid phone number.';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field as user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields at once on submit
    const errors = {};
    ['name', 'email', 'phone'].forEach(field => {
      const err = validateField(field, formData[field]);
      if (err) errors[field] = err;
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setError('');
    setLoading(true);


    try {
      if (isEditing) {
        await apiClient.put(`/clients/${id}`, formData);
      } else {
        await apiClient.post('/clients', formData);
      }
      navigate('/clients');
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} client`);
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="client-form-loading">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="client-form">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => navigate('/clients')}
          className="p-2 -ml-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          title="Back to clients">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="client-form-title">
            {isEditing ? 'Edit Client' : 'Add New Client'}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {isEditing ? 'Update client information' : 'Add a new client to your account'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-5 p-3 rounded-xl text-sm flex items-center gap-2"
          style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c' }}
          data-testid="client-error">
          <span>⚠️</span> {error}
        </div>
      )}

      <div className="card p-6 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
              <input
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="client-name-input"
                className={`input-modern ${fieldErrors.name ? 'border-red-500 ring-red-500' : ''}`}
                placeholder="John Smith"
              />
              {fieldErrors.name && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="client-email-input"
                className={`input-modern ${fieldErrors.email ? 'border-red-500 ring-red-500' : ''}`}
                placeholder="john@company.com"
              />
              {fieldErrors.email && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Company</label>
              <input
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                data-testid="client-company-input"
                className="input-modern"
                placeholder="Acme Inc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
              <input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                data-testid="client-phone-input"
                className={`input-modern ${fieldErrors.phone ? 'border-red-500 ring-red-500' : ''}`}
                placeholder="+1 (555) 000-0000"
              />
              {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">⚠ {fieldErrors.phone}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              data-testid="client-address-input"
              rows="3"
              className="input-modern resize-none"
              placeholder="123 Main St, City, State, ZIP"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="save-client-button"
              className="btn-primary flex items-center gap-2">
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isEditing ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isEditing ? '✓ Update Client' : '+ Save Client'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/clients')}
              data-testid="cancel-client-button"
              className="px-5 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;